import { DiagramComponent } from '../../core/DiagramComponent';
import type { SliderConfig, Variables, JXGBoard, JXGElement } from '../../types/interfaces';

interface ExtendedSliderConfig extends SliderConfig {
  label?: string;
  color?: string;
  strokeWidth?: number;
  gliderSize?: number;
  size?: number;
  snapWidth?: number;
  precision?: number;
  attributes?: Record<string, any>;
  tickStep?: number;
  tickColor?: string;
  tickLabelSize?: number;
  tickLabelColor?: string;
  tickLabelOffset?: number;
}

interface JXGSlider extends JXGElement {
  Value(): number;
  setValue(value: number): void;
}

/**
 * Slider component for numeric input
 * Supports integer and real modes with optional tick labels
 */
export class Slider extends DiagramComponent<ExtendedSliderConfig> {
  isInitializing: boolean = true;
  isDragging: boolean = false;

  constructor(config: ExtendedSliderConfig, board: JXGBoard, parentState: Variables = {}) {
    super(config, board, parentState);
  }

  render(): JXGSlider {
    const config = this.config;
    
    // Slider configuration
    const position = config.position || [0, 0, 5];
    const x1 = Number(this.evaluateExpression(position[0]));
    const y1 = Number(this.evaluateExpression(position[1]));
    const x2 = Number(this.evaluateExpression(position[2] || x1 + 5));
    const y2 = y1; // Horizontal slider
    
    const min = config.min || 0;
    const max = config.max || 10;
    const initialValue = Number(this.evaluateExpression(config.value || min));
    
    // Mode: 'integer' (default) or 'real'
    const mode = config.mode || 'integer';
    const snapWidth = mode === 'integer' ? 1 : (config.snapWidth || 0);
    
    // Glider (circle) configuration
    const gliderColor = config.gliderColor || config.color || '#3498db';
    const gliderOpacity = config.gliderOpacity !== undefined ? config.gliderOpacity : 0.7;
    const gliderSize = config.gliderSize || config.size || 6;
    
    // Create slider
    const slider = this.board.create('slider', [
      [x1, y1],
      [x2, y2],
      [min, initialValue, max]
    ], {
      name: config.label || '',
      snapWidth: snapWidth,
      strokeColor: config.color || '#3498db',
      strokeWidth: config.strokeWidth || 3,
      fillColor: gliderColor,
      fillOpacity: gliderOpacity,
      highlightStrokeColor: gliderColor, // Keep same color on highlight
      highlightFillColor: gliderColor, // Keep same color on highlight
      size: gliderSize,
      layer: 9,
      precision: mode === 'integer' ? 0 : (config.precision || 2),
      withTicks: false, // Disable JSXGraph's default ticks
      ticks: { visible: false }, // Ensure ticks are hidden
      baseline: {
        strokeColor: config.color || '#3498db',
        strokeWidth: config.strokeWidth || 3
      },
      highline: {
        strokeColor: config.color || '#3498db',
        strokeWidth: config.strokeWidth || 3
      },
      point1: { visible: false },
      point2: { visible: false },
      ...config.attributes
    }) as JXGSlider;
    
    this.jsxElements.push(slider);
    
    // Force update to ensure clean rendering
    this.board.update();
    
    // Add tick marks with labels if requested
    if (config.showTicks) {
      const tickStep = config.tickStep || 1;
      const showTickLabels = config.showTickLabels !== false;
      const tickLabelSize = config.tickLabelSize || 12;
      const tickLabelColor = config.tickLabelColor || '#666';
      const tickLabelOffset = config.tickLabelOffset || -0.5;
      
      for (let i = min; i <= max; i += tickStep) {
        const t = (i - min) / (max - min);
        const tickX = x1 + t * (x2 - x1);
        const tickY = y1;
        
        // Create small tick mark
        const tickMark = this.board.create('segment', [
          [tickX, tickY - 0.15],
          [tickX, tickY + 0.15]
        ], {
          strokeColor: config.tickColor || '#999',
          strokeWidth: 2,
          fixed: true,
          highlight: false,
          layer: 8
        });
        
        this.jsxElements.push(tickMark);
        
        // Add label if requested
        if (showTickLabels) {
          const label = this.board.create('text', [tickX, tickY + tickLabelOffset, i.toString()], {
            fontSize: tickLabelSize,
            color: tickLabelColor,
            anchorX: 'middle',
            anchorY: 'top',
            fixed: true,
            highlight: false,
            layer: 8,
            cssClass: 'jxg-text-no-pointer'
          });
          
          this.jsxElements.push(label);
        }
      }
    }
    
    // Update variable on drag
    if (config.variable) {
      slider.on('drag', () => {
        const rawValue = slider.Value();
        const value = mode === 'integer' ? Math.round(rawValue) : rawValue;
        this.localState[config.variable] = value;
        this._emitEvent('variable-change', { name: config.variable, value });
      });
      
      slider.on('down', () => {
        this.isDragging = true;
      });
      
      slider.on('up', () => {
        this.isDragging = false;
      });
      
      // Set initial variable value without triggering update
      const initValue = mode === 'integer' ? Math.round(initialValue) : initialValue;
      this.localState[config.variable] = initValue;
      
      // Emit initial value after a brief delay to ensure slider is fully rendered
      setTimeout(() => {
        this.isInitializing = false;
        this._emitEvent('variable-change', { name: config.variable, value: initValue });
      }, 0);
    }
    
    return slider;
  }

  /**
   * Update slider value without recreating
   */
  protected _rerender(): void {
    if (this.isDragging || this.isInitializing) {
      return;
    }
    
    if (this.jsxElements.length > 0) {
      const config = this.config;
      const slider = this.jsxElements[0] as JXGSlider;
      const newValue = Number(this.evaluateExpression(config.value));
      
      if (slider.Value() !== newValue) {
        slider.setValue(newValue);
        this.board.update();
      }
    } else {
      this.render();
    }
  }

  getSpecificity(): number {
    return 100; // Interactive element
  }
}
